import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { IconButton, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { getSearchData } from "../utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import { showPopupModal } from "@widgets/CommonModal";
import { StyleSettings } from "../../styles/StyleSettings";
import SinglePopup from "@widgets/commonpopup/SinglePopup";
import { showDialog } from "@widgets/CommonDialog";
import { ClearIcon } from "@mui/x-date-pickers";
import PostPopup from "@widgets/commonpopup/PostPopup";
import { global, onSearchStore } from "@shared/libs/Global";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";

interface SnStyleCodeNTextBoxProps {
  defaultValue?: any;
  code: string;
  cd_clas?: string;
  onValueChanged?: (code: string, name: string, rowData?: any) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
  popupConditions?: any;
}

const SnStyleCodeNTextBox = React.memo(
  ({
    code,
    cd_clas,
    defaultValue,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
    isSearchPanel = false,
    onFocus,
    popupConditions = {},
  }: SnStyleCodeNTextBoxProps) => {
    const { userInfo } = global();
    const [value, setValue] = useState(defaultValue);
    const [codeValue, setCodeValue] = useState(defaultValue?.code || "");
    const [nameValue, setNameValue] = useState(defaultValue?.name || "");
    const [borderWidth, setBorderWidth] = useState(isRequired ? "2px" : "1px");
    const [backgroundColor, setBackgroundColor] = useState(
      isEditable ? "white" : "#F1F1F1"
    );

    const { runOnSearch } = onSearchStore();

    // tab, enter, blur 이벤트 제어용
    const isOpenRef = useRef(false);

    // 팝업 세팅용
    const [title, setTitle] = useState("");
    const [autoSearch, setAutoSearch] = useState(true);

    // x버튼 조작용 state
    const [isMouseOver, setMouseOver] = useState(false);
    const [isFocused, setFocused] = useState(false);

    useEffect(() => {
      const singlePopupData = getSearchData(code, undefined);
      setTitle(singlePopupData.title);
      setAutoSearch(singlePopupData.autoSearch);
    }, [code]);

    useEffect(() => {
      if (defaultValue) {
        setValue(defaultValue);
        setCodeValue(defaultValue?.code || "");
        setNameValue(defaultValue?.name || "");
      }
    }, [defaultValue]);

    useEffect(() => setBorderWidth(isRequired ? "2px" : "1px"), [isRequired]);

    useEffect(
      () => setBackgroundColor(isEditable ? "white" : "#F1F1F1"),
      [isEditable]
    );

    const openPopup = async (inputValue?: string) => {
      if (isReadOnly) return showDialog("alter", "읽기 전용입니다.");
      if (isOpenRef.current) return;

      isOpenRef.current = true;
      try {
        const mergedData = {
          ...popupConditions,
          str_search: inputValue || nameValue,
          cd_clas: cd_clas || "",
        };

        let data: any;
        const result = await showPopupModal({
          title: title,
          children: (
            <SinglePopup
              code={code}
              selectedData={mergedData}
              autoSearch={autoSearch}
            />
          ),
        });
        data = handleReturnFunction(result);
        if (data === "close") {
          setValue({ code: codeValue, name: nameValue });
          setCodeValue(codeValue);
          setNameValue(nameValue);
          onValueChanged?.(codeValue, nameValue);
          onFocus && onFocus("current");
          return;
        } else if (data.length > 0) {
          setValue(data[0]);
          if (isSearchPanel) {
            setCodeValue(data[0].code);
            setNameValue(data[0].name);
            onValueChanged?.(data[0].code, data[0].name);
            onFocus && onFocus("current");
          } else {
            setCodeValue(data[0].code);
            setNameValue(data[0].name);
            onValueChanged?.(data[0].code, data[0].name);
            onFocus && onFocus("next");
          }
          await runOnSearch();
        } else {
          // 팝업에서 취소 눌렀을 때
          clearValue();
        }
      } finally {
        setTimeout(() => {
          isOpenRef.current = false;
        }, 100);
      }
    };

    const handleFocus = () => {
      setFocused(true);
    };

    const handleBlur = async (key?: string) => {
      setFocused(false);

      // 이미 팝업 열려있으면 return
      if (isOpenRef.current) return;

      if (!codeValue) {
        // 입력값이 없을 때
        if ((key === "Enter" || key === "searchBtn") && !isOpenRef.current) {
          await openPopup();
        } else {
          // setClearValue(true);
          clearValue();
          if (key === "Tab") onFocus && onFocus("next");
        }
      } else {
        // 입력값이 있을 때
        if (key === "searchBtn") {
          await openPopup(value.code || codeValue);
        } else {
          const data = await checkData();
          if (data && data.length === 1) {
            setValue(data[0]);
            onValueChanged?.(data[0].code, data[0].name);
            if (key === "Enter") {
              if (isSearchPanel) {
                // setNameValue(data[0].name);
                setFocused(true);
                await runOnSearch();

                onFocus && onFocus("current");
              } else {
                // setNameValue(data[0].name);
                onFocus && onFocus("next");
              }
            } else if (key === "Tab") {
              onFocus && onFocus("next");
            }
          } else if (!isOpenRef.current) {
            await openPopup(nameValue);
          }
        }
      }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setNameValue(e.target.value);
    };

    const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        await handleBlur(e.key);
      }
    };

    const checkData = async () => {
      try {
        const popupSetting = PopupSettings[code];
        if (!popupSetting) return;

        const param1 = {
          id: code.toLowerCase() + "_02",
          params: {
            cd_firm: userInfo.cd_firm,
            [popupSetting.str_search]: codeValue,
            cd_clas: cd_clas || "",
          },
        };
        const result1 = await fnGetDataSet(param1);
        if (result1[0]?.length === 1) return handleReturnFunction(result1[0]);

        const param2 = {
          id: code.toLowerCase() + "_03",
          params: {
            cd_firm: userInfo.cd_firm,
            str_search: codeValue,
            cd_clas: cd_clas || "",
          },
        };
        const result2 = await fnGetDataSet(param2);
        if (result2[0]?.length === 1) return handleReturnFunction(result2[0]);
      } catch (e) {}
    };

    const handleReturnFunction = (data: any) => {
      const popupSetting = PopupSettings[code];
      if (!popupSetting || !data) return [];
      if (data === "close") {
        return data;
      } else {
        return data.map((d: any) => ({
          ...d,
          code: d[popupSetting.columns[0]],
          name: d[popupSetting.columns[1]],
        }));
      }
    };

    const clearValue = () => {
      onValueChanged?.("", "", {});
      setValue({ code: "", name: "" });
      setCodeValue("");
      setNameValue("");
      // if (index && inputEls) inputEls.current[index].value = "";
    };

    const onLeft = async () => {
      const param = {
        id: "popup_style_04",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_biz: popupConditions.cd_biz
            ? popupConditions.cd_biz
            : userInfo.cd_biz,
          no_style: codeValue,
        },
      };
      const data: any = await fnGetDataSet(param);
      if (data && data[0] && data[0][0]) {
        setValue(data[0][0]);
        setCodeValue(data[0][0].no_style);
        setNameValue(data[0][0].nm_style);
        onValueChanged?.(data[0][0].no_style, data[0][0].nm_style);

        await runOnSearch();
      }
    };

    const onRight = async () => {
      const param = {
        id: "popup_style_05",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_biz: popupConditions.cd_biz
            ? popupConditions.cd_biz
            : userInfo.cd_biz,
          no_style: codeValue,
        },
      };
      const data: any = await fnGetDataSet(param);
      if (data && data[0] && data[0][0]) {
        setValue(data[0][0]);
        setCodeValue(data[0][0].no_style);
        setNameValue(data[0][0].nm_style);
        onValueChanged?.(data[0][0].no_style, data[0][0].nm_style);

        await runOnSearch();
      }
    };

    return (
      <div
        className="relative w-full flex justify-start"
        style={{ padding: 0 }}
      >
        <TextField
          inputRef={inputRef}
          value={codeValue}
          onFocus={handleFocus}
          onBlur={() => handleBlur()}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onMouseOver={() => setMouseOver(true)}
          onMouseOut={() => setMouseOver(false)}
          fullWidth={false}
          slotProps={{
            input: {
              autoComplete: "off",
              readOnly: isReadOnly,
              endAdornment: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "50%",
                  }}
                >
                  <IconButton
                    onClick={(e) => {
                      onLeft();
                    }}
                    edge="end"
                    tabIndex={-1}
                    size="small"
                  >
                    <ArrowBackIosNew fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      handleBlur("searchBtn");
                    }}
                    edge="end"
                    tabIndex={-1}
                    size="small"
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={(e) => {
                      onRight();
                    }}
                    edge="end"
                    tabIndex={-1}
                    size="small"
                  >
                    <ArrowForwardIos fontSize="small" />
                  </IconButton>
                </div>
              ),
            },
          }}
          sx={{
            backgroundColor,
            borderWidth,
            "& .MuiOutlinedInput-root": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
              paddingRight: 0,
            },
            "& .MuiInputBase-input": {
              fontSize: StyleSettings.font,
            },
          }}
        />
        <TextField
          value={nameValue}
          slotProps={{
            input: {
              autoComplete: "off",
              style: {
                height: StyleSettings.control_height,
                fontSize: StyleSettings.font,
              },
            },
          }}
          fullWidth
          disabled={true}
          sx={{
            backgroundColor: "#dadcdc",
            "& .MuiOutlinedInput-root": {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              "& fieldset": {
                borderLeft: "none",
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
            },
            "& .MuiInputBase-input": {
              fontSize: StyleSettings.font,
            },
          }}
        />
      </div>
    );
  }
);
export default SnStyleCodeNTextBox;
